'use client';

import { useEffect, useState } from 'react';
import { SignInTitle } from '@/app_components/SignIn/Components/SignInSharedComponents';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import type { SignInApiResponse } from '@/app_lib/types/accounts/signinApi';
import { getDeviceForTracking } from '@/app_lib/utils/deviceUtils';
import { setSessionStorageObject } from '@/app_lib/utils/sessionStorageUtils';

interface SignInPasswordlessConfirmationBaseProps {
  headerText: string;
  subheaderText: string;
  resendEmail: string;
}

export const SignInPasswordlessConfirmationBase = ({
  headerText,
  subheaderText,
  resendEmail,
}: SignInPasswordlessConfirmationBaseProps) => {
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    if (timeLeft <= 0) {
      setError(null);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 1) {
          return prev - 1;
        }
        setError(null);
        return 0;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  useEffect(() => {
    if (error) {
      setMessage(null);
    }
  }, [error]);

  useEffect(() => {
    trackEvent(
      {
        deviceType: getDeviceForTracking(),
        eventAction: 'account_signin_step_2',
        loginType: 'passwordless',
      },
      'signInEventTracker',
    );

    setSessionStorageObject('__pwlessSI', { initiated: true });
  }, []);

  const handleResend = async () => {
    const res = await fetch('/api/signin/login', {
      body: JSON.stringify({ email: resendEmail, password: '' }),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    });
    const response: SignInApiResponse = await res.json();
    if (response.success && response.userType === 'passwordless') {
      setError(null);
      setMessage('Email sent successfully!');
      setTimeLeft(0);
      return;
    }
    if (!response.success && response.message) {
      const match = response.message.match(/(\d+)\s*seconds?/i);
      if (match) {
        setTimeLeft(parseInt(match[1], 10));
      }
      setError(response.message);
    }
  };

  return (
    <div className='flex w-full flex-col items-center layout-gap-md font-graphik-compact'>
      <div className='flex flex-col items-center gap-sm w-full text-center'>
        <SignInTitle title={headerText} />
        <div className='typography-sm'>{subheaderText}</div>
      </div>
      <div className='flex flex-col items-center gap-sm w-full text-center'>
        <p className='typography-sm'>Didn&apos;t get it? Wait a minute, then resend.</p>
        <button
          className='typography-default font-medium cursor-pointer disabled:cursor-not-allowed'
          disabled={timeLeft > 0}
          onClick={handleResend}
          type='button'
        >
          Resend email
        </button>
        {error && (
          <div className='typography-sm text-red'>
            {timeLeft > 0 ? `Please wait ${timeLeft} seconds before sending this email again.` : error}
          </div>
        )}
        {message && <p className='typography-sm'>{message}</p>}
      </div>
    </div>
  );
};
